import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronDown, Copy, Download, FileDown, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import WhatsAppPresupuestoModal from '@/components/WhatsAppPresupuestoModal';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import { TotalizadorCostos } from './TotalizadorCostos';
import { ModalCopiarPresupuesto, type ModalCopiarModo } from './ModalCopiarPresupuesto';
import { ModalImportarPresupuesto } from './ModalImportarPresupuesto';
import { useCostos } from '@/hooks/useCostos';
import { useCostosPermissions } from '@/hooks/useCostosPermissions';
import { BotonDesasociarPresupuesto } from '@/presupuestos/components/BotonDesasociarPresupuesto';
import { BotonExportar } from '@/presupuestos/components/BotonExportar';
import { CajaItems } from '@/presupuestos/components/CajaItems';
import { PARTES } from '@/presupuestos/constants/presupuestos';
import { rutaDetallePresupuesto } from '@/presupuestos/constants/routes';
import { confirmarItemsSinDestino, contarSinDestino } from '@/presupuestos/utils/destinoPago';
import { useDestinosPagoOpciones } from '@/presupuestos/hooks/useDestinosPago';
import { usePresupuestosProformaPermissions } from '@/presupuestos/hooks/usePresupuestosProformaPermissions';
import { costoService } from '@/services/costoService';
import type { CostoFormData, ParteItem } from '@/types/costo';

interface SeccionPlanDeCostosProps {
  tramiteId: string;
  /** Trámite con sus partes; habilita el envío por WhatsApp. */
  tramite?: any;
  isSplitView?: boolean;
  splitContainer?: HTMLElement | null;
}

/**
 * Presupuesto de una escritura, presentado en dos cajas independientes
 * (adquiriente / transferente) con sus subtotales, además del totalizador global.
 */
export const SeccionPlanDeCostos: React.FC<SeccionPlanDeCostosProps> = ({
  tramiteId,
  tramite,
  isSplitView = false,
  splitContainer = null,
}) => {
  const [modalCopiarOpen, setModalCopiarOpen] = useState(false);
  const [modalImportarOpen, setModalImportarOpen] = useState(false);
  const [modoCopiar, setModoCopiar] = useState<ModalCopiarModo>('exportar');
  const [menuWhatsAppOpen, setMenuWhatsAppOpen] = useState(false);
  const [modalWhatsAppOpen, setModalWhatsAppOpen] = useState(false);
  const [parteWhatsApp, setParteWhatsApp] = useState<ParteItem>('adquiriente');

  const permissions = useCostosPermissions();
  const permisosProforma = usePresupuestosProformaPermissions();
  const { canSendWhatsApp } = useAuthorization();
  const { opciones: destinosPago } = useDestinosPagoOpciones();
  const {
    costos,
    totales,
    totalesPorParte,
    presupuestoVinculado,
    isLoading,
    crearCosto,
    actualizarCosto,
    eliminarCosto,
    copiarPresupuesto,
    importarProforma,
    reordenarCostos,
    isCreando,
    isActualizando,
    isEliminando,
    isCopiando,
    isImportando,
  } = useCostos(tramiteId);

  const anyLoading = isCreando || isActualizando || isEliminando || isCopiando || isImportando;

  const handleAbrirCopiar = (modo: ModalCopiarModo) => {
    setModoCopiar(modo);
    setModalCopiarOpen(true);
  };

  const handleAbrirWhatsApp = (parte: ParteItem) => {
    // Cerrar el menú y diferir cualquier diálogo (la confirmación de ítems sin
    // destino y el envío): si un Dialog (Radix) se abre en el mismo tick que se
    // cierra el DropdownMenu, o que se cierra otro Dialog, ambos pisan
    // `pointer-events` de <body> y la página queda sin poder tocarse.
    setMenuWhatsAppOpen(false);

    setTimeout(async () => {
      if (!(await confirmarItemsSinDestino(contarSinDestino(costos, [parte]), 'Enviar'))) return;

      setParteWhatsApp(parte);
      setTimeout(() => setModalWhatsAppOpen(true), 100);
    }, 100);
  };

  const handleConfirmarCopia = async (otraEscrituraId: string) => {
    const targetTramiteId = modoCopiar === 'exportar' ? otraEscrituraId : tramiteId;
    const sourceTramiteId = modoCopiar === 'exportar' ? tramiteId : otraEscrituraId;

    const resultado = await copiarPresupuesto({ targetTramiteId, sourceTramiteId });

    toast.success(
      modoCopiar === 'exportar'
        ? `Se copiaron ${resultado.copiados} item${resultado.copiados !== 1 ? 's' : ''} a la escritura destino.`
        : `Se importaron ${resultado.copiados} item${resultado.copiados !== 1 ? 's' : ''} al presupuesto actual.`
    );

    setModalCopiarOpen(false);
  };

  const handleImportarProforma = async (
    presupuestoProformaId: string,
    reemplazar: boolean
  ) => {
    const resultado = await importarProforma({ presupuestoProformaId, reemplazar });

    toast.success(
      `Se importaron ${resultado.importados} ítem${resultado.importados !== 1 ? 's' : ''} de "${resultado.presupuestoOrigen.nombre}".`
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // No mostrar sección si el usuario no tiene permisos
  if (!permissions.hasAnyPermission) {
    return null;
  }

  const itemsDeParte = (parte: ParteItem) => costos.filter((costo) => costo.parte === parte);

  // El PDF toma el encabezado (partes, inmueble, base imponible) del presupuesto
  // importado: sin uno no se puede exportar.
  const proformaOrigenId = costos.find((costo) => costo.origenProformaId)?.origenProformaId;

  return (
    <div className="@container space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <h2 className="text-xl font-semibold">Presupuesto</h2>
          {presupuestoVinculado && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>
                Vinculado a:{' '}
                <Link
                  to={rutaDetallePresupuesto(presupuestoVinculado.id)}
                  className="font-medium text-blue-600 hover:underline"
                >
                  {presupuestoVinculado.nombre}
                </Link>
              </span>
              {permisosProforma.canImport && (
                <BotonDesasociarPresupuesto
                  tramiteId={tramiteId}
                  nombrePresupuesto={presupuestoVinculado.nombre}
                  disabled={anyLoading}
                />
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {permisosProforma.canExport && (
            <BotonExportar
              presupuestoId={proformaOrigenId ?? ''}
              nombrePresupuesto="Presupuesto de la escritura"
              disabled={!proformaOrigenId || anyLoading}
              title={proformaOrigenId ? undefined : 'Para exportar, importe primero un presupuesto'}
              exportar={(parte) => costoService.exportarPresupuesto(tramiteId, parte)}
              confirmarExportacion={(partes) =>
                confirmarItemsSinDestino(contarSinDestino(costos, partes))
              }
            />
          )}

          {canSendWhatsApp && tramite && (
            // El botón deshabilitado no recibe eventos: el tooltip va en el contenedor.
            <div title={proformaOrigenId ? undefined : 'Para enviar, importe primero un presupuesto'}>
              <DropdownMenu open={menuWhatsAppOpen} onOpenChange={setMenuWhatsAppOpen}>
                <DropdownMenuTrigger asChild>
                  <Button disabled={!proformaOrigenId || anyLoading} size="sm" variant="outline">
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Enviar por WhatsApp
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {PARTES.map((parte) => (
                    <DropdownMenuItem key={parte} onSelect={() => handleAbrirWhatsApp(parte)}>
                      Enviar al {parte}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {permissions.canCreate && (
            <div className="flex flex-wrap items-center gap-2">
              {permisosProforma.canImport && (
                <Button
                  onClick={() => setModalImportarOpen(true)}
                  disabled={anyLoading}
                  size="sm"
                  variant="outline"
                  title="Importar un presupuesto independiente confirmado"
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  Importar presupuesto
                </Button>
              )}
              <Button
                onClick={() => handleAbrirCopiar('importar')}
                disabled={anyLoading}
                size="sm"
                variant="outline"
                title="Importar presupuesto desde otra escritura"
              >
                <Download className="h-4 w-4 mr-2" />
                Importar de otra escritura
              </Button>
              <Button
                onClick={() => handleAbrirCopiar('exportar')}
                disabled={anyLoading}
                size="sm"
                variant="outline"
                title="Copiar presupuesto a otra escritura"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copiar
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Se mide el contenedor y no el viewport: en vista dividida el panel puede
          ser mucho más angosto que la pantalla. */}
      <div className="grid grid-cols-1 @3xl:grid-cols-3 gap-4">
        {/* Izquierda: las dos cajas (2/3 del ancho en desktop) */}
        <div className="space-y-4 @3xl:col-span-2">
          {PARTES.map((parte) => (
            <CajaItems
              key={parte}
              parte={parte}
              items={itemsDeParte(parte)}
              subtotales={totalesPorParte[parte]}
              onCrearItem={(data: CostoFormData) => crearCosto({ ...data, parte })}
              onActualizarItem={(costoId, data) => actualizarCosto({ costoId, data })}
              onEliminarItem={(costoId) => void eliminarCosto(costoId)}
              isLoading={anyLoading}
              canCreate={permissions.canCreate}
              canEdit={permissions.canEdit}
              canDelete={permissions.canDelete}
              onReordenar={permissions.canEdit ? reordenarCostos : undefined}
              destinosPago={destinosPago}
              cotizacionDolar={presupuestoVinculado?.cotizacionDolar ?? null}
              isSplitView={isSplitView}
              splitContainer={splitContainer}
            />
          ))}
        </div>

        {/* Derecha: Totalizador (1/3 del ancho en desktop) */}
        <div className="@3xl:col-span-1">
          <TotalizadorCostos totales={totales} />
        </div>
      </div>

      {/* Modal copiar/importar entre escrituras */}
      <ModalCopiarPresupuesto
        isOpen={modalCopiarOpen}
        onClose={() => setModalCopiarOpen(false)}
        onConfirm={handleConfirmarCopia}
        tramiteActualId={tramiteId}
        modo={modoCopiar}
        isLoading={isCopiando}
      />

      {canSendWhatsApp && tramite && (
        <WhatsAppPresupuestoModal
          open={modalWhatsAppOpen}
          onOpenChange={setModalWhatsAppOpen}
          tramite={tramite}
          parte={parteWhatsApp}
        />
      )}

      {/* Modal importar presupuesto independiente */}
      <ModalImportarPresupuesto
        isOpen={modalImportarOpen}
        onClose={() => setModalImportarOpen(false)}
        onConfirm={handleImportarProforma}
        isLoading={isImportando}
      />
    </div>
  );
};
